import { z } from "zod";
import { EscalationSubStatusSchema } from "@remi/enums/escalation-sub-status";
import { TicketPrioritySchema } from "@remi/enums/ticket/priority";
import { TicketStatusSchema } from "@remi/enums/ticket/status";
import { isOneOf } from "@remi/ts-utils";

import { DefinitionSchema, SortDefinitionSchema } from "./query-builder";

const stringArraySchema = z.object({
  value: z.array(z.string()),
  comparator: z.enum(["in", "not_in", "is_null", "is_not_null"]).optional(),
});
// const numberArraySchema = z.object({ value: z.array(z.number()) });
const dateRangeSchema = z.object({
  value: z.object({ from: z.date(), to: z.date().optional() }).optional(),
  comparator: z.enum(["between", "gt", "lt", "is_between", "is_null", "is_not_null", "relative"]).optional(),
  relativeDays: z.int().positive().optional(),
});
const booleanSchema = z.object({ value: z.boolean().optional() });
type DateRange = z.infer<typeof dateRangeSchema>;
type StringArray = z.infer<typeof stringArraySchema>;
type BooleanFilter = z.infer<typeof booleanSchema>;

/**
 * * * PROJECT TABLE * * *
 */
export const ProjectTableColumnIdValues = [
  "id",
  "status",
  "homeowner",
  "phase",
  "productionPhase",
  "deadline",
  "phaseDuration",
  "sowType",
  "category",
  "currentTasks",
  "quoteOwner",
  "installDate",
  "contractor",
  "source",
  "partnerFlow",
  "gsv",
  "milestone",
  "availableStartDate",
  "projectReceivedDate",
  "estimatedPermitCompletion",
  "schedulingAvailableDate",
  "productionScheduledStartDate",
  "workCompletedDate",
  "bidCategory",
  "projectType",
  "scheduleWorkDueDate",
  "state",
  "address",
  "createdAt",
  "projectDeadline",
] as const;

const ProjectTableColumnIdSchema = z.enum(ProjectTableColumnIdValues);
export type ProjectTableColumnId = z.infer<typeof ProjectTableColumnIdSchema>;
const ProjectTableFilterIdValues = [
  "status",
  "phase",
  "owner",
  "installDate",
  "contractor",
  "source",
  "partnerFlow",
  "sowType",
  "category",
  "milestone",
  "state",
  "deadline",
  "taskOwnership",
  "cpm",
] as const;
const _ProjectTableFilterIdSchema = z.enum(ProjectTableFilterIdValues);
type ProjectTableFilterId = z.infer<typeof _ProjectTableFilterIdSchema>;

type ProjectTableFilterRecord = Record<ProjectTableFilterId, DateRange | StringArray>;

export const ProjectTableFilterSchema = z.object({
  status: stringArraySchema,
  phase: stringArraySchema,
  productionPhase: stringArraySchema,
  owner: stringArraySchema,
  installDate: dateRangeSchema,
  contractor: stringArraySchema,
  source: stringArraySchema,
  partnerFlow: stringArraySchema,
  sowType: stringArraySchema,
  category: stringArraySchema,
  region: stringArraySchema,
  milestone: stringArraySchema,
  state: stringArraySchema,
  deadline: dateRangeSchema,
  taskOwnership: stringArraySchema,
  cpm: stringArraySchema,
}) satisfies z.ZodType<ProjectTableFilterRecord>;

const ProjectSortIdValues = [
  "id",
  "status",
  "homeowner",
  "category",
  "projectType",
  "createdAt",
  "deadline",
  "projectDeadline",
  "state",
  "phaseDuration",
  "quoteOwner",
  "installDate",
  "contractor",
  "phase",
  "sowType",
  "source",
  "milestone",
  "gsv",
  "projectReceivedDate",
  "estimatedPermitCompletion",
  "productionScheduledStartDate",
  "workCompletedDate",
  "availableStartDate",
  "scheduleWorkDueDate",
  "currentTasks",
] as const satisfies ProjectTableColumnId[];
export type ProjectSortId = (typeof ProjectSortIdValues)[number];
export const ProjectSortIdSchema = z.enum(ProjectSortIdValues);

export const ProjectTableColumnDisplayNames: Record<ProjectTableColumnId, string> = {
  status: "Project status",
  id: "Remi ID",
  category: "Category",
  homeowner: "Homeowner name",
  createdAt: "Created",
  deadline: "Project deadline",
  phase: "Sub-phase",
  productionPhase: "Production phase",
  phaseDuration: "Time in sub-phase",
  currentTasks: "Current tasks",
  quoteOwner: "Quote owner",
  installDate: "Work date",
  contractor: "Contractor",
  source: "Source",
  partnerFlow: "Partner Flow",
  address: "Address",
  state: "State",
  sowType: "SOW type",
  milestone: "Milestone",
  gsv: "GSV",
  availableStartDate: "Available start date",
  projectReceivedDate: "Project received date",
  estimatedPermitCompletion: "Estimated start week",
  schedulingAvailableDate: "Available date",
  productionScheduledStartDate: "Scheduled start date",
  workCompletedDate: "Work completed date",
  bidCategory: "Bid category",
  projectType: "Project type",
  scheduleWorkDueDate: "Schedule work follow-up",
  projectDeadline: "Project deadline",
};

export const ProjectSortSchema = z.object({
  id: ProjectSortIdSchema,
  desc: z.boolean(),
});

export const ProjectSearchInputSchema = z.object({
  search: z.string(),
  columns: z.array(ProjectTableColumnIdSchema).default([]),
  filters: ProjectTableFilterSchema.partial(),
  sort: z.array(ProjectSortSchema).default([]),
  includeFilters: z.boolean().default(true),
  cursor: z.number().nullish(),
  includeCompletedTasks: z.boolean().default(false),
});

export type ProjectSearchInput = z.infer<typeof ProjectSearchInputSchema>;

export const ProjectMilestoneSearchInputSchema = ProjectSearchInputSchema.extend({
  isNotActionable: z.boolean().optional(),
});

export type ProjectMilestoneSearchInput = z.infer<typeof ProjectMilestoneSearchInputSchema>;

export const GroupableProjectColumnIdValues = [
  "category",
  "sowType",
  "source",
  "partnerFlow",
  "deadline",
  "phase",
  "quoteOwner",
  "installDate",
  "contractor",
  "state",
  "createdAt",
] as const satisfies ProjectTableColumnId[];
const GroupableProjectColumnIdSchema = z.enum(GroupableProjectColumnIdValues);
export type GroupableProjectColumnId = z.infer<typeof GroupableProjectColumnIdSchema>;

const DateGroupableColumnIdValues = [
  "deadline",
  "installDate",
  "createdAt",
] as const satisfies GroupableProjectColumnId[];
export type DateGroupableColumnId = (typeof DateGroupableColumnIdValues)[number];
export const isDateGroupColumn = (columnId: GroupableProjectColumnId): columnId is DateGroupableColumnId =>
  isOneOf(columnId, DateGroupableColumnIdValues);

export const DATE_BUCKET_KEYS = ["Overdue", "Today", "This week", "This month", "Later"] as const;
export type DateBucketKey = (typeof DATE_BUCKET_KEYS)[number];

const DateBucketBoundariesSchema = z.object({
  todayStart: z.date(),
  tomorrowStart: z.date(),
  weekEndExclusive: z.date(),
  monthEndExclusive: z.date(),
});
export type DateBucketBoundaries = z.infer<typeof DateBucketBoundariesSchema>;

const MAX_BUCKET_BOUNDARY_SPAN_MS = 60 * 24 * 60 * 60 * 1000;

const addBoundaryIssues = (boundaries: DateBucketBoundaries, ctx: z.RefinementCtx, path: string) => {
  if (boundaries.todayStart >= boundaries.tomorrowStart) {
    ctx.addIssue({ code: "custom", message: "todayStart must be before tomorrowStart", path: [path] });
  }
  const times = [
    boundaries.todayStart,
    boundaries.tomorrowStart,
    boundaries.weekEndExclusive,
    boundaries.monthEndExclusive,
  ].map((d) => d.getTime());
  if (Math.max(...times) - Math.min(...times) > MAX_BUCKET_BOUNDARY_SPAN_MS) {
    ctx.addIssue({ code: "custom", message: "date bucket boundary span exceeds 60 days", path: [path] });
  }
};

export const GetGroupedSearchCountsInputSchema = z
  .object({
    search: z.string(),
    filters: ProjectTableFilterSchema.partial(),
    groupBy: GroupableProjectColumnIdSchema,
    dateBucketBoundaries: DateBucketBoundariesSchema.optional(),
  })
  .superRefine((data, ctx) => {
    const dateColumn = isDateGroupColumn(data.groupBy);
    if (dateColumn && data.dateBucketBoundaries === undefined) {
      ctx.addIssue({
        code: "custom",
        message: "dateBucketBoundaries is required when grouping by a date column",
        path: ["dateBucketBoundaries"],
      });
    }
    if (!dateColumn && data.dateBucketBoundaries !== undefined) {
      ctx.addIssue({
        code: "custom",
        message: "dateBucketBoundaries is only valid when grouping by a date column",
        path: ["dateBucketBoundaries"],
      });
    }
    if (data.dateBucketBoundaries !== undefined) {
      addBoundaryIssues(data.dateBucketBoundaries, ctx, "dateBucketBoundaries");
    }
  });

export type GetGroupedSearchCountsInput = z.infer<typeof GetGroupedSearchCountsInputSchema>;

const GroupedSearchCountsOutputSchema = z.array(z.object({ key: z.string().nullable(), count: z.number() }));
export type GroupedSearchCountsOutput = z.infer<typeof GroupedSearchCountsOutputSchema>;

export const ProjectSearchWithGroupInputSchema = ProjectSearchInputSchema.extend({
  group: z
    .object({
      columnId: GroupableProjectColumnIdSchema,
      key: z.string().nullable(),
      dateBucketBoundaries: DateBucketBoundariesSchema.optional(),
    })
    .optional(),
}).superRefine((data, ctx) => {
  const group = data.group;
  if (group === undefined) {
    return;
  }
  const dateColumn = isDateGroupColumn(group.columnId);
  if (dateColumn) {
    if (group.key !== null && !isOneOf(group.key, DATE_BUCKET_KEYS)) {
      ctx.addIssue({
        code: "custom",
        message: "group.key must be a date bucket name or null for date columns",
        path: ["group", "key"],
      });
    }
    if (group.dateBucketBoundaries === undefined) {
      ctx.addIssue({
        code: "custom",
        message: "group.dateBucketBoundaries is required when grouping by a date column",
        path: ["group", "dateBucketBoundaries"],
      });
    }
  } else if (group.dateBucketBoundaries !== undefined) {
    ctx.addIssue({
      code: "custom",
      message: "group.dateBucketBoundaries is only valid when grouping by a date column",
      path: ["group", "dateBucketBoundaries"],
    });
  }
});

export type ProjectSearchWithGroupInput = z.infer<typeof ProjectSearchWithGroupInputSchema>;

/**
 * * * TASK TABLE * * *
 */
const TaskTableColumnIdValues = [
  "projectId",
  "type",
  "status",
  "readiness",
  "assignee",
  "team",
  "responsibleParty",
  "permittingEntity",
  "contractor",
  "duration",
  "dueDate",
  "projectStatus",
  "sowType",
  "projectCategory",
  "homeowner",
  "source",
  "partnerFlow",
  "address",
  "netTerms",
  "paymentDue",
  "deadline",
  "currentPhaseStartDate",
  "homeownerSentiment",
  "isReadyForAudit",
  "quoteType",
  "waitingOn",
  "redesignStatus",
] as const;

const TaskSortIdValues = [
  "projectId",
  "type",
  "status",
  "assignee",
  "permittingEntity",
  "contractor",
  "duration",
  "dueDate",
  "projectStatus",
  "sowType",
  "projectCategory",
  "source",
  "netTerms",
  "deadline",
  "paymentDue",
  "currentPhaseStartDate",
  "homeownerSentiment",
] as const satisfies TaskTableColumnId[];
export const TaskSortIdSchema = z.enum(TaskSortIdValues);
export type TaskSortId = (typeof TaskSortIdValues)[number];
export const TaskSortSchema = z.object({
  id: z.enum(TaskSortIdValues),
  desc: z.boolean(),
});

const _TaskTableFilterIdValues = [
  "status",
  "assignee",
  "taskType",
  "projectStatus",
  "deadline",
  "dueDate",
  "source",
  "partnerFlow",
  "sowType",
  "projectCategory",
  "responsibleParty",
  "permittingOwner",
  "contractor",
  "netTerms",
  "paymentDue",
  "region",
  "homeownerSentiment",
  "cpm",
  "projectId",
  "team",
  "readiness",
  "waitingOn",
  "lastAttemptReason",
] as const;
type TaskTableFilterId = (typeof _TaskTableFilterIdValues)[number];
type TaskTableFilterRecord = Record<TaskTableFilterId, DateRange | StringArray>;
export const TaskTableFilterSchema = z.object({
  status: stringArraySchema,
  assignee: stringArraySchema,
  taskType: stringArraySchema,
  projectStatus: stringArraySchema,
  dueDate: dateRangeSchema,
  source: stringArraySchema,
  partnerFlow: stringArraySchema,
  sowType: stringArraySchema,
  projectCategory: stringArraySchema,
  responsibleParty: stringArraySchema,
  permittingOwner: stringArraySchema,
  contractor: stringArraySchema,
  netTerms: stringArraySchema,
  deadline: dateRangeSchema,
  paymentDue: dateRangeSchema,
  region: stringArraySchema,
  remiLabor: booleanSchema,
  homeownerSentiment: stringArraySchema,
  isReadyForAudit: booleanSchema,
  needsReview: booleanSchema,
  cpm: stringArraySchema,
  quoteType: stringArraySchema,
  projectId: stringArraySchema,
  team: stringArraySchema,
  readiness: stringArraySchema,
  waitingOn: stringArraySchema,
  lastAttemptReason: stringArraySchema,
}) satisfies z.ZodType<TaskTableFilterRecord>;

const TaskTableColumnIdSchema = z.enum(TaskTableColumnIdValues);
export type TaskTableColumnId = z.infer<typeof TaskTableColumnIdSchema>;

export const UNASSIGNED = "unassigned";

export const TaskSearchInputSchema = z.object({
  search: z.string(),
  sort: z.array(TaskSortSchema),
  filters: TaskTableFilterSchema.partial(),
  includeFilters: z.boolean(),
  cursor: z.number().nullish(),
});

export type TaskSearchInput = z.infer<typeof TaskSearchInputSchema>;

export const GroupableTaskColumnIdValues = [
  "status",
  "readiness",
  "sowType",
  "source",
  "projectCategory",
  "assignee",
  "dueDate",
  "partnerFlow",
  "responsibleParty",
  "contractor",
  "permittingEntity",
  "currentPhaseStartDate",
] as const satisfies TaskTableColumnId[];
const GroupableTaskColumnIdSchema = z.enum(GroupableTaskColumnIdValues);
export type GroupableTaskColumnId = z.infer<typeof GroupableTaskColumnIdSchema>;

const DateGroupableTaskColumnIdValues = ["dueDate", "currentPhaseStartDate"] as const satisfies GroupableTaskColumnId[];
export type DateGroupableTaskColumnId = (typeof DateGroupableTaskColumnIdValues)[number];
export const isDateGroupTaskColumn = (columnId: GroupableTaskColumnId): columnId is DateGroupableTaskColumnId =>
  isOneOf(columnId, DateGroupableTaskColumnIdValues);

export const GetGroupedTaskSearchCountsInputSchema = z
  .object({
    search: z.string(),
    filters: TaskTableFilterSchema.partial(),
    groupBy: GroupableTaskColumnIdSchema,
    dateBucketBoundaries: DateBucketBoundariesSchema.optional(),
  })
  .superRefine((data, ctx) => {
    const dateColumn = isDateGroupTaskColumn(data.groupBy);
    if (dateColumn && data.dateBucketBoundaries === undefined) {
      ctx.addIssue({
        code: "custom",
        message: "dateBucketBoundaries is required when grouping by a date column",
        path: ["dateBucketBoundaries"],
      });
    }
    if (!dateColumn && data.dateBucketBoundaries !== undefined) {
      ctx.addIssue({
        code: "custom",
        message: "dateBucketBoundaries is only valid when grouping by a date column",
        path: ["dateBucketBoundaries"],
      });
    }
    if (data.dateBucketBoundaries !== undefined) {
      addBoundaryIssues(data.dateBucketBoundaries, ctx, "dateBucketBoundaries");
    }
  });

export type GetGroupedTaskSearchCountsInput = z.infer<typeof GetGroupedTaskSearchCountsInputSchema>;

export const TaskSearchWithGroupInputSchema = TaskSearchInputSchema.extend({
  group: z
    .object({
      columnId: GroupableTaskColumnIdSchema,
      key: z.string().nullable(),
      dateBucketBoundaries: DateBucketBoundariesSchema.optional(),
    })
    .optional(),
}).superRefine((data, ctx) => {
  const group = data.group;
  if (group === undefined) {
    return;
  }
  const dateColumn = isDateGroupTaskColumn(group.columnId);
  if (dateColumn) {
    if (group.key !== null && !isOneOf(group.key, DATE_BUCKET_KEYS)) {
      ctx.addIssue({
        code: "custom",
        message: "group.key must be a date bucket name or null for date columns",
        path: ["group", "key"],
      });
    }
    if (group.dateBucketBoundaries === undefined) {
      ctx.addIssue({
        code: "custom",
        message: "group.dateBucketBoundaries is required when grouping by a date column",
        path: ["group", "dateBucketBoundaries"],
      });
    }
  } else if (group.dateBucketBoundaries !== undefined) {
    ctx.addIssue({
      code: "custom",
      message: "group.dateBucketBoundaries is only valid when grouping by a date column",
      path: ["group", "dateBucketBoundaries"],
    });
  }
});

export type TaskSearchWithGroupInput = z.infer<typeof TaskSearchWithGroupInputSchema>;

/**
 * * * MATERIALS ORDER TABLE * * *
 */

const MaterialsOrderTableFilterIdValues = [
  "vendor",
  "billStatus",
  "hasBill",
  "hasInvoice",
  "hasVendorCredit",
  "createdAt",
  "estimatedDeliveryDate",
  "actualDeliveryDate",
] as const;

type MaterialsOrderTableFilterId = (typeof MaterialsOrderTableFilterIdValues)[number];

type MaterialsOrderTableFilterRecord = Record<MaterialsOrderTableFilterId, DateRange | StringArray | BooleanFilter>;

export const MaterialsOrderTableFilterSchema = z.object({
  vendor: stringArraySchema,
  billStatus: stringArraySchema,
  hasBill: booleanSchema,
  hasInvoice: booleanSchema,
  hasVendorCredit: booleanSchema,
  createdAt: dateRangeSchema,
  estimatedDeliveryDate: dateRangeSchema,
  actualDeliveryDate: dateRangeSchema,
}) satisfies z.ZodType<MaterialsOrderTableFilterRecord>;

export type MaterialsOrderTableFilters = Partial<z.infer<typeof MaterialsOrderTableFilterSchema>>;

const MaterialsOrderSortIdValues = [
  "externalId",
  "externalInvoiceId",
  "projectId",
  "orderTotal",
  "vendor",
  "estimatedDeliveryDate",
  "actualDeliveryDate",
  "createdAt",
] as const;

export type MaterialsOrderSortId = (typeof MaterialsOrderSortIdValues)[number];
export const MaterialsOrderSortIdSchema = z.enum(MaterialsOrderSortIdValues);

export const MaterialsOrderSortSchema = z.object({
  id: MaterialsOrderSortIdSchema,
  desc: z.boolean(),
});

/**
 * * * BID TABLE * * *
 */

const BidTableColumnIdValues = [
  "status",
  "bidId",
  "homeowner",
  "phase",
  "productionPhase",
  "currentTasks",
  "currentProjects",
  "quoteOwner",
  "hsa",
  "address",
  "status",
  "homeownerSentiment",
  "source",
  "partnerFlow",
  "projectSpecialist",
  "adjuster",
  "lossType",
  "lossDate",
] as const;

const BidTableColumnIdSchema = z.enum(BidTableColumnIdValues);
export type BidTableColumnId = z.infer<typeof BidTableColumnIdSchema>;

const BidTableFilterIdValues = [
  "phase",
  "source",
  "quoteOwner",
  "hsa",
  "projectSpecialist",
  "status",
  "homeownerSentiment",
] as const satisfies BidTableColumnId[];
const _BidTableFilterIdSchema = z.enum(BidTableFilterIdValues);
type BidTableFilterId = z.infer<typeof _BidTableFilterIdSchema>;

type BidTableFilterRecord = Record<BidTableFilterId, DateRange | StringArray | BooleanFilter>;

const BidTableFilterSchema = z.object({
  status: stringArraySchema,
  phase: stringArraySchema,
  productionPhase: stringArraySchema,
  quoteOwner: stringArraySchema,
  hsa: stringArraySchema,
  source: stringArraySchema,
  region: stringArraySchema,
  buyer: stringArraySchema,
  homeownerSentiment: stringArraySchema,
  partnerFlow: stringArraySchema,
  projectSpecialist: stringArraySchema,
}) satisfies z.ZodType<BidTableFilterRecord>;

const BidSortIdValues = ["phase", "source", "quoteOwner"] as const satisfies BidTableColumnId[];
const BidSortIdSchema = z.enum(BidSortIdValues);

const BidSortSchema = z.object({
  id: BidSortIdSchema,
  desc: z.boolean(),
});
export const BidSearchInputSchema = z.object({
  search: z.string(),
  filters: BidTableFilterSchema.partial(),
  sort: z.array(BidSortSchema),
  includeFilters: z.boolean().default(true),
  cursor: z.number().nullish(),
  searchIndexOptions: z
    .object({
      useFullAddress: z.boolean().default(false),
      useHomeownerPhone: z.boolean().default(false),
      useBuyerIds: z.boolean().default(false),
    })
    .optional(),
});

export type BidSearchInput = z.infer<typeof BidSearchInputSchema>;

/**
 * * * INBOX TABLE * * *
 */

const InboxItemTypeValues = ["TICKET", "CONVERSATION", "TASK", "ESCALATION"] as const;
export const InboxItemTypeSchema = z.enum(InboxItemTypeValues);
export type InboxItemType = z.infer<typeof InboxItemTypeSchema>;
export const InboxItemType = InboxItemTypeSchema.enum;

export const InboxBulkRowSchema = z.object({
  source: InboxItemTypeSchema,
  id: z.string().min(1),
});
export type InboxBulkRow = z.infer<typeof InboxBulkRowSchema>;

export const INBOX_BULK_MAX_ROWS = 200;
const InboxBulkRowsSchema = z.array(InboxBulkRowSchema).min(1).max(INBOX_BULK_MAX_ROWS);

export const InboxBulkReassignAssigneeInputSchema = z.object({
  rows: InboxBulkRowsSchema,
  assigneeId: z.string().min(1).nullable(),
});
export type InboxBulkReassignAssigneeInput = z.infer<typeof InboxBulkReassignAssigneeInputSchema>;

export const InboxBulkReassignTeamInputSchema = z.object({
  rows: InboxBulkRowsSchema,
  teamId: z.string().min(1),
});
export type InboxBulkReassignTeamInput = z.infer<typeof InboxBulkReassignTeamInputSchema>;

export const InboxBulkSetFollowUpInputSchema = z.object({
  rows: InboxBulkRowsSchema,
  followUpDate: z.coerce.date().nullable(),
});
export type InboxBulkSetFollowUpInput = z.infer<typeof InboxBulkSetFollowUpInputSchema>;

export const InboxBulkSetTicketStatusInputSchema = z.object({
  rows: InboxBulkRowsSchema,
  status: TicketStatusSchema,
});
export type InboxBulkSetTicketStatusInput = z.infer<typeof InboxBulkSetTicketStatusInputSchema>;

export const InboxBulkSetTicketPriorityInputSchema = z.object({
  rows: InboxBulkRowsSchema,
  priority: TicketPrioritySchema,
});
export type InboxBulkSetTicketPriorityInput = z.infer<typeof InboxBulkSetTicketPriorityInputSchema>;

export const InboxBulkSetTicketTagsInputSchema = z.object({
  rows: InboxBulkRowsSchema,
  tagIds: z.array(z.string().min(1)).min(1).max(50),
});
export type InboxBulkSetTicketTagsInput = z.infer<typeof InboxBulkSetTicketTagsInputSchema>;

export const InboxBulkResolveConversationsInputSchema = z.object({
  rows: InboxBulkRowsSchema,
});
export type InboxBulkResolveConversationsInput = z.infer<typeof InboxBulkResolveConversationsInputSchema>;

export const InboxBulkCompleteTasksInputSchema = z.object({
  rows: InboxBulkRowsSchema,
});
export type InboxBulkCompleteTasksInput = z.infer<typeof InboxBulkCompleteTasksInputSchema>;

export const InboxBulkSetEscalationSubStatusInputSchema = z.object({
  rows: InboxBulkRowsSchema,
  subStatus: EscalationSubStatusSchema.nullable(),
});
export type InboxBulkSetEscalationSubStatusInput = z.infer<typeof InboxBulkSetEscalationSubStatusInputSchema>;

export interface InboxBulkUpdateInput {
  rows: InboxBulkRow[];
  assigneeId?: string | null;
  teamId?: string;
  followUpDate?: Date | null;
  isResolved?: boolean;
}

export interface InboxBulkResult {
  succeeded: number;
  failed: number;
  failedRows: InboxBulkRow[];
}

export const InboxSearchInputSchema = z.object({
  search: z.string(),
  ticketDefinition: DefinitionSchema.optional(),
  conversationDefinition: DefinitionSchema.optional(),
  taskDefinition: DefinitionSchema.optional(),
  escalationDefinition: DefinitionSchema.optional(),
  sort: SortDefinitionSchema,
  cursor: z.string().max(1024).nullish(),
});

export type InboxSearchInput = z.infer<typeof InboxSearchInputSchema>;

const INBOX_COUNTS_MAX_ITEMS = 40;
const INBOX_COUNTS_MAX_KEY_LENGTH = 128;
// DefinitionSchema is recursively unbounded and a batch compiles into one statement, so the item cap alone
// leaves an amplification dimension open.
const INBOX_COUNTS_MAX_CONDITION_NODES = 50;
const INBOX_COUNTS_MAX_DEPTH = 5;

const INBOX_COUNTS_DEFINITION_KEYS = [
  "ticketDefinition",
  "conversationDefinition",
  "taskDefinition",
  "escalationDefinition",
] as const;

const InboxCountsItemInputSchema = InboxSearchInputSchema.omit({ cursor: true }).extend({
  search: z.literal(""),
});

const isDefinitionShape = (node: unknown): node is { conditions: unknown[] } =>
  typeof node === "object" && node !== null && "conditions" in node && Array.isArray(node.conditions);

const measureDefinition = (node: unknown, depth: number): { nodes: number; depth: number } => {
  if (!isDefinitionShape(node)) {
    return { nodes: 1, depth };
  }
  let nodes = 1;
  let maxDepth = depth;
  for (const child of node.conditions) {
    const measured = measureDefinition(child, depth + 1);
    nodes += measured.nodes;
    maxDepth = Math.max(maxDepth, measured.depth);
  }
  return { nodes, depth: maxDepth };
};

export const InboxCountsInputSchema = z.object({
  items: z
    .array(
      z
        .object({
          key: z
            .string()
            .min(1)
            .max(INBOX_COUNTS_MAX_KEY_LENGTH)
            .regex(/^[A-Za-z0-9:_-]+$/),
          input: InboxCountsItemInputSchema,
        })
        .superRefine((item, ctx) => {
          const present = INBOX_COUNTS_DEFINITION_KEYS.filter((key) => item.input[key] !== undefined);
          if (present.length === 0) {
            // Rejected rather than counted as 0: a zero-block item satisfies the assignee-anchor rule
            // vacuously and would cache under the shared TTL, breaking the every-entry-is-personal invariant.
            ctx.addIssue({ code: "custom", path: ["input"], message: "At least one definition block is required" });
          }
          let nodes = 0;
          let depth = 0;
          for (const key of present) {
            const measured = measureDefinition(item.input[key], 1);
            nodes += measured.nodes;
            depth = Math.max(depth, measured.depth);
          }
          if (nodes > INBOX_COUNTS_MAX_CONDITION_NODES) {
            ctx.addIssue({ code: "custom", path: ["input"], message: "Definition is too complex" });
          }
          if (depth > INBOX_COUNTS_MAX_DEPTH) {
            ctx.addIssue({ code: "custom", path: ["input"], message: "Definition is nested too deeply" });
          }
        }),
    )
    .min(1)
    .max(INBOX_COUNTS_MAX_ITEMS)
    .refine((items) => new Set(items.map((item) => item.key)).size === items.length, {
      message: "Duplicate keys",
    }),
});

export type InboxCountsInput = z.infer<typeof InboxCountsInputSchema>;

export interface InboxCountsOutputItem {
  key: string;
  count: number;
}

const BidRequestTableColumnIdValues = [
  "status",
  "bidId",
  "homeownerName",
  "claimNumber",
  "worksiteAddress",
  "adjuster",
  "estimator",
  "lossType",
  "lossDate",
  "policyNumber",
  "deductible",
  "rotationTrade",
  "jobSize",
  "partnerFlow",
  "externalId",
  "estimateTotal",
  "estimateUpdatedAt",
  "partnerOrganizationName",
  "estimateOnly",
  "amountPaidToPolicyholder",
] as const;

export const BidRequestTableColumnIdSchema = z.enum(BidRequestTableColumnIdValues);
export const BidRequestTableColumnId = BidRequestTableColumnIdSchema.enum;
export type BidRequestTableColumnId = z.infer<typeof BidRequestTableColumnIdSchema>;

const BidRequestTableFilterIdValues = [
  "status",
  "partnerFlow",
  "adjuster",
  "estimator",
  "lossType",
  "lossDate",
  "partnerOrganizationName",
  "jobSize",
  "rotationTrade",
  "estimateOnly",
] as const satisfies BidRequestTableColumnId[];

const BidRequestTableFilterIdSchema = z.enum(BidRequestTableFilterIdValues);
type BidRequestTableFilterId = z.infer<typeof BidRequestTableFilterIdSchema>;

const BidRequestSortIdValues = [
  "status",
  "adjuster",
  "estimator",
  "homeownerName",
  "lossDate",
  "estimateTotal",
  "estimateUpdatedAt",
] as const satisfies BidRequestTableColumnId[];
export const BidRequestSortIdSchema = z.enum(BidRequestSortIdValues);
export const BidRequestSortId = BidRequestSortIdSchema.enum;
export type BidRequestSortId = z.infer<typeof BidRequestSortIdSchema>;

type BidRequestTableFilterRecord = Record<BidRequestTableFilterId, DateRange | StringArray | BooleanFilter>;

const BidRequestTableFilterSchema = z.object({
  status: stringArraySchema,
  partnerFlow: stringArraySchema,
  adjuster: stringArraySchema,
  lossType: stringArraySchema,
  lossDate: dateRangeSchema,
  estimator: stringArraySchema,
  partnerOrganizationName: stringArraySchema,
  jobSize: stringArraySchema,
  rotationTrade: stringArraySchema,
  estimateOnly: booleanSchema,
}) satisfies z.ZodType<BidRequestTableFilterRecord>;

export const BidRequestSearchInputSchema = z.object({
  search: z.string(),
  filters: BidRequestTableFilterSchema.partial(),
  sort: SortDefinitionSchema,
  includeFilters: z.boolean().default(true),
  cursor: z.number().nullish(),
});

export type BidRequestSearchInput = z.infer<typeof BidRequestSearchInputSchema>;

/**
 * * * ACTIONABLE TABLE * * *
 */

const ActionableTableColumnIdValues = [
  "type",
  "status",
  "assignee",
  "team",
  "dueDate",
  "resolvedAt",
  "resolvedBy",
  "createdBy",
  "createdAt",
  "ingestionEventId",
] as const;

const ActionableTableColumnIdSchema = z.enum(ActionableTableColumnIdValues);
export type ActionableTableColumnId = z.infer<typeof ActionableTableColumnIdSchema>;

const ActionableTableFilterIdValues = ["type", "status", "team", "assignee"] as const;
const ActionableTableFilterIdSchema = z.enum(ActionableTableFilterIdValues);
type ActionableTableFilterId = z.infer<typeof ActionableTableFilterIdSchema>;

type ActionableTableFilterRecord = Record<ActionableTableFilterId, DateRange | StringArray>;

export const ActionableTableFilterSchema = z.object({
  type: stringArraySchema,
  status: stringArraySchema,
  team: stringArraySchema,
  assignee: stringArraySchema,
  dueDate: dateRangeSchema,
  createdAt: dateRangeSchema,
}) satisfies z.ZodType<ActionableTableFilterRecord>;

const ActionableSortIdValues = [
  "type",
  "status",
  "dueDate",
  "resolvedAt",
  "createdAt",
] as const satisfies ActionableTableColumnId[];
export type ActionableSortId = (typeof ActionableSortIdValues)[number];
const ActionableSortIdSchema = z.enum(ActionableSortIdValues);

export const ActionableSortSchema = z.object({
  id: ActionableSortIdSchema,
  desc: z.boolean(),
});

export const ActionableTableColumnDisplayNames: Record<ActionableTableColumnId, string> = {
  type: "Type",
  status: "Status",
  assignee: "Assignee",
  team: "Team",
  dueDate: "Due date",
  resolvedAt: "Resolved at",
  resolvedBy: "Resolved by",
  createdBy: "Created by",
  createdAt: "Created at",
  ingestionEventId: "Ingestion Event ID",
};

export const ActionableSearchInputSchema = z.object({
  search: z.string(),
  filters: ActionableTableFilterSchema.partial(),
  sort: z.array(ActionableSortSchema),
  includeFilters: z.boolean().default(true),
  cursor: z.number().nullish(),
});

export type ActionableSearchInput = z.infer<typeof ActionableSearchInputSchema>;
